import { StrictMode, useSyncExternalStore } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/query-client";
import { router } from "./router";
import { useAuth } from "./lib/auth";
import { captureInstallPrompt } from "./lib/deferred-install-prompt";
import { setupNetworkStatus } from "./lib/network-status";
import { setupSessionSync } from "./lib/session-sync";
import { useCompanyStore } from "./store";
import "./styles/app.css";

// Before the first render, deliberately: `beforeinstallprompt` fires once and
// never re-fires, and everything React does first (hydration gate, token
// refresh, persona probes) happens after the browser would already have fired
// it.
captureInstallPrompt();

// Likewise before the first render: this SEEDS `onlineManager` from
// `navigator.onLine`, and a document that loaded offline reports online until it
// does — including to the route guard and the persona probes that run first.
setupNetworkStatus();

// Sibling-tab session convergence: adopt a sibling's token rotation into this
// tab's store, and propagate a sibling's sign-out through the full teardown.
setupSessionSync();

const { onFinishHydration, hasHydrated } = useCompanyStore.persist;

function useHasHydrated(): boolean {
  return useSyncExternalStore(onFinishHydration, hasHydrated, () => false);
}

function App() {
  const hydrated = useHasHydrated();
  const auth = useAuth();

  if (!hydrated) return null;

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} context={{ auth }} />
    </QueryClientProvider>
  );
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
